import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import axios from 'axios';

const emptySection = {
  section_key: '',
  label: '',
  title: '',
  body: '',
  display_order: 0,
  is_highlight: 0,
};

const AboutCommunityManager = () => {
  const token = localStorage.getItem('token');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSections = useCallback(() => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/2.0/about-community`)
      .then((res) => setSections(res.data.data || []))
      .catch((err) => {
        console.error('Error loading about content', err);
        alert('Unable to load about content');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  const updateSection = (index, field, value) => {
    setSections((prev) =>
      prev.map((section, i) =>
        i === index
          ? {
              ...section,
              [field]: value,
            }
          : section
      )
    );
  };

  const addSection = () => {
    setSections((prev) => [
      ...prev,
      {
        ...emptySection,
        section_key: `section_${Date.now()}`,
        display_order: prev.length + 1,
      },
    ]);
  };

  const saveSections = () => {
    setSaving(true);
    axios
      .put(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/about-community`,
        { sections },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        alert('About content updated successfully');
        fetchSections();
      })
      .catch((err) => {
        console.error('Error saving about content', err);
        alert(err?.response?.data?.message || 'Unable to save about content');
      })
      .finally(() => setSaving(false));
  };

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        About Udyog Community Content
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage the write-up shown on the Udyog Connect About page.
      </Typography>

      <Box sx={{ display: 'grid', gap: 2 }}>
        {sections.map((section, index) => (
          <Box key={section.section_key || index} sx={{ p: 2, border: '1px solid #ccc', borderRadius: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Section Key"
                value={section.section_key}
                onChange={(e) => updateSection(index, 'section_key', e.target.value)}
                size="small"
                helperText="Example: mission, vision, founded"
              />
              <TextField
                label="Label"
                value={section.label}
                onChange={(e) => updateSection(index, 'label', e.target.value)}
                size="small"
              />
              <TextField
                label="Display Order"
                type="number"
                value={section.display_order}
                onChange={(e) => updateSection(index, 'display_order', Number(e.target.value))}
                size="small"
              />
              <TextField
                label="Highlight Section"
                select
                SelectProps={{ native: true }}
                value={Number(section.is_highlight)}
                onChange={(e) => updateSection(index, 'is_highlight', Number(e.target.value))}
                size="small"
              >
                <option value={0}>No</option>
                <option value={1}>Yes</option>
              </TextField>
            </Box>

            <TextField
              label="Title"
              value={section.title}
              onChange={(e) => updateSection(index, 'title', e.target.value)}
              fullWidth
              sx={{ mt: 2 }}
              size="small"
            />
            <TextField
              label="Body"
              value={section.body}
              onChange={(e) => updateSection(index, 'body', e.target.value)}
              fullWidth
              multiline
              minRows={4}
              sx={{ mt: 2 }}
            />
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={addSection}>
          Add Section
        </Button>
        <Button variant="contained" onClick={saveSections} disabled={saving}>
          {saving ? 'Saving...' : 'Save About Content'}
        </Button>
      </Box>
    </Box>
  );
};

export default AboutCommunityManager;
